/**
 * Trustpilot Invitations API Retention Pre-Flight (Story 35.3 AC #4)
 *
 * Trustpilot's Invitations API retains invitation events for **30 days
 * from send date** per [Trustpilot Invitations API documentation](https://documentation-apidocumentation.trustpilot.com/invitation-api)
 * (re-verified via `developers.trustpilot.com/invitations-api-overview/`
 * 2026-05-21; the documented `invitation-data/delete` endpoint accepts
 * a "date older than X" parameter — consistent with the 30-day retention
 * semantics).
 *
 * # SCOPE-NARROWED to ONLY the Invitations API endpoint group
 *
 * This is the **SECOND adapter** in the library with a retention
 * pre-flight (after CallRail 22.1's 25-month ceiling). **CRITICAL
 * divergence from CallRail**: CallRail blanket-applies its retention
 * pre-flight to every adapter call; Trustpilot applies it ONLY to the
 * Invitations API endpoint group. Service Reviews + Business Unit
 * Details have NO retention cap (Story 23.3 AC #4 invariant
 * carry-forward — `legal.trustpilot.com/end-user-terms-and-conditions`
 * + `business.trustpilot.com/terms` verified 2026-05-19; reviews
 * persist for the lifetime of the business unit).
 *
 * **DO NOT** copy this helper's blanket-reject pattern to Service
 * Reviews or Business Unit Details — those endpoints accept arbitrary
 * historical ranges.
 *
 * # The 3 outcomes
 *
 *   - `{ skip: true }` — dateRange entirely OUTSIDE the retention window
 *     (`end < now - 30 days`). Caller MUST silently skip Batch 2 and
 *     emit a forensic structured log `trustpilot_invitations_skipped_outside_retention`.
 *   - `{ skip: false, clamped: { start, end } }` — dateRange partially
 *     overlaps (`start < now - 30 days <= end`). Caller MUST use the
 *     clamped `start` for the Invitations API call ONLY and emit a
 *     `trustpilot_invitations_date_range_clamped` forensic log.
 *   - `{ skip: false }` — dateRange entirely INSIDE the retention window.
 *     Caller proceeds without modification.
 *
 * The helper is PURE — no `Date.now()` inside, no side effects. Caller
 * supplies `now` for deterministic testing (mirrors CallRail 22.1's
 * `validateCallRailDateRangeAgainstRetention(dateRange, now)`
 * signature).
 */

/** Trustpilot Invitations API retention cap — 30 days from send date. */
export const TRUSTPILOT_INVITATIONS_RETENTION_DAYS = 30;
const RETENTION_MS = TRUSTPILOT_INVITATIONS_RETENTION_DAYS * 24 * 60 * 60 * 1000;

/**
 * Result of pre-flight retention check. See module JSDoc for semantics.
 */
export interface TrustpilotInvitationsRetentionResult {
  /** True when the dateRange is entirely outside the retention window. */
  readonly skip: boolean;
  /** Present when the dateRange partially overlaps — caller uses this. */
  readonly clamped?: { readonly start: Date; readonly end: Date };
  /**
   * Forensic — populated when skip or clamp fires; absent otherwise.
   *
   * Possible values:
   *   - `"outside_retention"` — Case 1: range ends before retention cutoff.
   *   - `"partial_overlap_clamped"` — Case 2: range straddles retention
   *     cutoff; `start` clamped to cutoff. May also include future-end
   *     adjustment in the same `clamped` tuple.
   *   - `"future_end_clamped"` — Case 3: range entirely within retention
   *     but `end > now`; `end` clamped to now. (Round 2 fix R2-6 — distinct
   *     from retention clamp so operators can attribute log events.)
   *   - `"future_only"` — Case 0 / Round 2 fix R2-1: range entirely in the
   *     future. Skipped (caller bug or wall-clock skew).
   */
  readonly reason?:
    | "outside_retention"
    | "partial_overlap_clamped"
    | "future_end_clamped"
    | "future_only";
  /** The retention cutoff used (now - 30 days). Returned for logging. */
  readonly retentionCutoff: Date;
}

/**
 * Compute the 30-day retention cutoff. `now - 30 days` to-the-millisecond.
 * UTC-anchored (mirrors the queue scheduler's UTC operation).
 *
 * @param now - Caller-supplied "now" for deterministic testing.
 */
export function computeTrustpilotInvitationsRetentionCutoff(now: Date): Date {
  const nowMs = now.getTime();
  if (!Number.isFinite(nowMs)) {
    // Pass 1 defense — Invalid Date inputs would propagate NaN to
    // downstream arithmetic and silently mis-classify dateRange.
    throw new TypeError(
      "computeTrustpilotInvitationsRetentionCutoff: `now` must be a finite Date"
    );
  }
  return new Date(nowMs - RETENTION_MS);
}

/**
 * Pre-flight: classify the dateRange against the 30-day Invitations
 * retention window. See module JSDoc for the 3 possible outcomes.
 *
 * `start === retentionCutoff` is INCLUSIVE — passes without clamp.
 *
 * @param dateRange - Caller-supplied report window.
 * @param now - Caller-supplied "now" (deterministic-testing seam).
 *
 * @throws TypeError when `start > end` (caller bug — should be caught
 *   at fetchMetrics top per Story 23.4 Round 2 fix; helper does NOT
 *   silently accept inverted ranges).
 * @throws TypeError when start or end are Invalid Date.
 */
export function validateTrustpilotInvitationsDateRange(
  dateRange: { start: Date; end: Date },
  now: Date
): TrustpilotInvitationsRetentionResult {
  const startMs = dateRange.start.getTime();
  const endMs = dateRange.end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    throw new TypeError(
      "validateTrustpilotInvitationsDateRange: dateRange.start and dateRange.end must be finite Dates"
    );
  }
  if (startMs > endMs) {
    throw new TypeError(
      `validateTrustpilotInvitationsDateRange: dateRange.start (${dateRange.start.toISOString()}) must be on or before dateRange.end (${dateRange.end.toISOString()})`
    );
  }

  const cutoff = computeTrustpilotInvitationsRetentionCutoff(now);
  const cutoffMs = cutoff.getTime();

  // Code-review round 1 fix #29 + round 2 fix R2-1 — Invitations API
  // cannot return data for future dates. Clamp end-of-range to `now` so a
  // caller bug or wall-clock skew does not smuggle a future `endDate`
  // into the URL.
  //
  // Critical Round 2 invariant: after future-end-clamp the effective end
  // could fall BEHIND the requested start (when both start AND end are
  // future-dated). Detect this BEFORE the retention cutoff checks and
  // return `skip: true` with the `future_only` reason — the helper MUST
  // never return an inverted clamped pair.
  const nowMs = now.getTime();
  const effectiveEndMs = Math.min(endMs, nowMs);

  // Case 0 (round 2 fix R2-1): the requested range was entirely in the
  // future. After the future-end-clamp, no past data could match. Skip.
  if (startMs > nowMs) {
    return {
      skip: true,
      reason: "future_only",
      retentionCutoff: cutoff,
    };
  }

  // Case 1: range entirely outside retention — end < cutoff.
  if (effectiveEndMs < cutoffMs) {
    return {
      skip: true,
      reason: "outside_retention",
      retentionCutoff: cutoff,
    };
  }

  // Case 2: partial overlap with retention — start < cutoff. Clamp start.
  // The end may also need clamping if `end > now`; preserve both
  // adjustments in the single clamped tuple.
  if (startMs < cutoffMs) {
    return {
      skip: false,
      clamped: {
        start: cutoff,
        end: effectiveEndMs < endMs ? new Date(effectiveEndMs) : dateRange.end,
      },
      reason: "partial_overlap_clamped",
      retentionCutoff: cutoff,
    };
  }

  // Case 3: range entirely inside retention. Future-end clamp may still
  // produce a clamped range if `end > now`. Round 2 fix R2-6 — surface
  // a distinct reason so operators can tell future-clamp from retention-
  // clamp in the structured-log payload.
  if (effectiveEndMs < endMs) {
    return {
      skip: false,
      clamped: { start: dateRange.start, end: new Date(effectiveEndMs) },
      reason: "future_end_clamped",
      retentionCutoff: cutoff,
    };
  }

  return {
    skip: false,
    retentionCutoff: cutoff,
  };
}
